import {
  asCurve,
  closestPoint,
  type Curve,
  edgeCount,
  hostCurve,
  intersectCurves,
  parameterAt,
  solidEdges,
} from '@/domain/construction';
import { clamp, type Vec2, vec } from '@/domain/math';
import { type EntityId, isUnbounded, kindOf } from '@/domain/model';
import { hitTestAll, type Scene, valueOf } from '@/engine';
import { chooseSpacing, DEFAULT_GRID_STYLE } from '@/rendering';

/**
 * Snapping.
 *
 * Snapping is not a convenience here, it is the mechanism by which constructions get
 * built. When a new point lands on a circle it does not merely *look* attached — it
 * becomes a **locus point** on that circle, and stays exactly on it forever after
 * (ADR-0003). Dropping a point near a curve and having it be independent would make the
 * editor a drawing program.
 *
 * ## Priority
 *
 * 1. An existing point — reuse it rather than stacking a duplicate on top.
 * 2. An intersection of two curves — the single most valuable construction in geometry.
 * 3. A point on a curve — attaches as a locus point.
 * 4. The grid.
 * 5. Free space.
 *
 * The order is a strict preference, not a distance contest: a vertex two pixels further
 * away than a grid line is still what the user meant.
 */

export const SnapKind = {
  None: 'none',
  Grid: 'grid',
  Point: 'point',
  Curve: 'curve',
  Intersection: 'intersection',
  /**
   * An edge of a named solid — a line in space, not on the page.
   *
   * Its own kind rather than another `Curve`, because what comes back is a pair of *corners*
   * rather than a host curve, and the point built from it is a division of those corners so
   * that it knows where it is in space. A caller that treated it as a curve would build a flat
   * mark on the projection and lose exactly the thing that makes a solid a solid.
   */
  SolidEdge: 'solid-edge',
  /**
   * A corner of a solid — the point in space, not the dot on the page.
   *
   * Its own kind, and ahead of {@link SolidEdge}, because a corner is where two or three edges
   * end and asking for one meant asking for whichever edge happened to be nearest, at parameter
   * zero. What came back was a *division* of that edge sitting exactly on top of the corner: a
   * second entity for a place the figure already had, and the one the click then selected — so
   * naming the corner named the wrong thing.
   *
   * A solid built from a drawn polygon, or written in the script, already names every corner. A
   * solid dropped from the toolbar names none, and this is how one gets picked up.
   */
  SolidVertex: 'solid-vertex',
} as const;
export type SnapKind = (typeof SnapKind)[keyof typeof SnapKind];

export interface SnapResult {
  readonly world: Vec2;
  readonly kind: SnapKind;
  /** The existing point that was snapped to, when `kind` is `point`. */
  readonly entity?: EntityId;
  /** Host curve and parameter, when `kind` is `curve` — enough to build a locus point. */
  readonly host?: EntityId;
  readonly parameter?: number;
  /**
   * Which side of the host, when the host is a polygon. Absent when it is a curve in itself.
   *
   * Carried through the snap rather than worked out again by whoever builds the point: the
   * side chosen here is the one under the pointer, and re-deriving it from the position is
   * ambiguous at a corner, where two sides are equally close and only one was aimed at.
   */
  readonly edge?: number;
  /** The two corners the edge joins, when `kind` is `solid-edge`. Indices into the solid. */
  readonly corners?: readonly [number, number];
  /** Which corner of the solid, when `kind` is `solid-vertex`. An index into `host`. */
  readonly corner?: number;
  /** The two curves crossing, when `kind` is `intersection`. */
  readonly crossing?: readonly [EntityId, EntityId];
  /** Index of the chosen crossing in the intersection routine's stable ordering. */
  readonly crossingIndex?: number;
}

export interface SnapSettings {
  readonly enabled: boolean;
  readonly toGrid: boolean;
  readonly toEntities: boolean;
  /** Pick radius in screen pixels; converted to world by the caller's tolerance. */
  readonly radiusPixels: number;
}

export const DEFAULT_SNAP: SnapSettings = Object.freeze({
  enabled: true,
  toGrid: true,
  toEntities: true,
  radiusPixels: 10,
});

export interface SnapContext {
  readonly scene: Scene;
  readonly zoom: number;
  /** Entities to ignore — the ones currently being dragged. */
  readonly exclude?: ReadonlySet<EntityId>;
  readonly settings?: SnapSettings;
}

const FREE = (world: Vec2): SnapResult => ({ world, kind: SnapKind.None });

export function snap(context: SnapContext, world: Vec2): SnapResult {
  const settings = context.settings ?? DEFAULT_SNAP;
  if (!settings.enabled) return FREE(world);

  const tolerance = settings.radiusPixels / context.zoom;

  if (settings.toEntities) {
    const entitySnap = snapToEntities(context, world, tolerance);
    if (entitySnap !== null) return entitySnap;
  }

  if (settings.toGrid) {
    const spacing = chooseSpacing(context.zoom, DEFAULT_GRID_STYLE.targetSpacing);
    const snapped = {
      x: Math.round(world.x / spacing) * spacing,
      y: Math.round(world.y / spacing) * spacing,
    };
    if (vec.distance(world, snapped) <= tolerance) {
      return { world: snapped, kind: SnapKind.Grid };
    }
  }

  return FREE(world);
}

function snapToEntities(context: SnapContext, world: Vec2, tolerance: number): SnapResult | null {
  const hits = hitTestAll(context.scene, world, tolerance, {
    ...(context.exclude === undefined ? {} : { exclude: context.exclude }),
  });
  if (hits.length === 0) return null;

  // 1. An existing point wins outright.
  const point = hits.find((hit) => hit.kind === 'point');
  if (point !== undefined) {
    return { world: point.closest, kind: SnapKind.Point, entity: point.id };
  }

  const curves = hits.filter((hit) => asCurve(valueOf(context.scene, hit.id)) !== null);

  // 2. Two curves crossing near the pointer.
  if (curves.length >= 2) {
    const crossing = nearestCrossing(context, curves, world, tolerance);
    if (crossing !== null) return crossing;
  }

  // 3. On a single curve, as a locus point.
  const best = curves[0];
  if (best !== undefined) {
    const host = asCurve(valueOf(context.scene, best.id));
    if (host !== null) {
      return {
        world: best.closest,
        kind: SnapKind.Curve,
        host: best.id,
        parameter: parameterAt(host, best.closest),
      };
    }
  }

  // 4. On the side of a polygon, which is a curve without being one.
  const side = nearestPolygonEdge(context, hits, world, tolerance);
  if (side !== null) return side;

  /*
   * 5. A corner of a solid, before 6 and not after it.
   *
   * A corner is where two or three edges end, so an edge is always available there and always
   * the less specific answer: asking for the corner used to hand back the nearest edge at
   * parameter zero, and the point built from that was a *division* of the edge sitting exactly
   * on top of a corner the figure already had.
   */
  const corner = nearestSolidVertex(context, hits, world, tolerance);
  if (corner !== null) return corner;

  // 6. On the edge of a named solid, which is a line in space rather than on the page.
  return nearestSolidEdge(context, hits, world, tolerance);
}

/**
 * The nearest named edge of a solid, reported as the two corners it joins.
 *
 * ## Why this reports corners and not a curve
 *
 * Because a solid's edge is a line **in space**, and the drawn segment is only its shadow. A
 * point attached to the shadow would be a flat mark with no position in space, and this product
 * spends most of its care on exactly what that position decides: whether a line through the
 * point is hidden by the body, whether an angle at it is truly right rather than right-looking,
 * and where it goes when the solid turns. All three would be wrong, and wrong invisibly — the
 * figure looks correct from the angle it was built at.
 *
 * Naming the two corners lets the point be built as a division of them, which is interpolated in
 * space as well as on the page. That is the same reason `ratio-point` exists rather than being a
 * locus point on a segment.
 *
 * Last of all the tiers: a drawn segment, a polygon's side, and every real curve win first.
 */
/**
 * The nearest corner of a solid under the pointer.
 *
 * Read off the solid's projected points rather than off the ends of its edges, so a corner with
 * no drawn edge to it — the far side of a sphere's tessellation, a base ring hidden behind the
 * body — is still the same corner with the same index. The index is what makes it addressable:
 * `solid-vertex` names a solid and a number, and evaluates to the point in *space*.
 */
function nearestSolidVertex(
  context: SnapContext,
  hits: readonly { id: EntityId; closest: Vec2 }[],
  world: Vec2,
  tolerance: number
): SnapResult | null {
  let best: SnapResult | null = null;
  let bestDistance = tolerance;

  for (const hit of hits) {
    const value = valueOf(context.scene, hit.id);
    if (value?.kind !== 'solid') continue;

    const points = value.points ?? [];
    for (const [index, point] of points.entries()) {
      const distance = vec.distance(world, point);
      if (distance >= bestDistance) continue;

      bestDistance = distance;
      best = { world: point, kind: SnapKind.SolidVertex, host: hit.id, corner: index };
    }
  }

  return best;
}

function nearestSolidEdge(
  context: SnapContext,
  hits: readonly { id: EntityId; closest: Vec2 }[],
  world: Vec2,
  tolerance: number
): SnapResult | null {
  let best: SnapResult | null = null;
  let bestDistance = tolerance;

  for (const hit of hits) {
    for (const edge of solidEdges(valueOf(context.scene, hit.id))) {
      const curve: Curve = { kind: 'segment', segment: edge.segment };
      const at = closestPoint(curve, world);
      const distance = vec.distance(world, at);
      if (distance >= bestDistance) continue;

      bestDistance = distance;
      best = {
        world: at,
        kind: SnapKind.SolidEdge,
        host: hit.id,
        corners: [edge.a, edge.b],
        parameter: parameterAt(curve, at),
      };
    }
  }

  return best;
}

/**
 * The nearest side of any polygon under the pointer, as something a point can attach to.
 *
 * ## Why this is not just another entry in `curves`
 *
 * Two reasons, and the second is a real trap.
 *
 * A polygon is a **region**, so `asCurve` refuses it — rightly, since "the curve of a triangle"
 * is not a thing. Its boundary is three curves, and the one the user is pointing at has to be
 * chosen before anything can be parameterised.
 *
 * And the distance a hit test reports for a polygon is **zero anywhere inside it** — that is
 * what makes a filled shape grabbable by its middle. Feeding that into a snap would attach a
 * point clicked in the centre of a triangle to whichever side happened to be listed first. So
 * the distance is measured again here, against the ring only, and the tolerance applied to
 * that.
 *
 * Last of the entity tiers, so a drawn segment lying along a polygon's edge still wins: the
 * user drew that segment, and a point on it is a point on the thing they made.
 */
function nearestPolygonEdge(
  context: SnapContext,
  hits: readonly { id: EntityId; closest: Vec2 }[],
  world: Vec2,
  tolerance: number
): SnapResult | null {
  let best: SnapResult | null = null;
  let bestDistance = tolerance;

  for (const hit of hits) {
    const value = valueOf(context.scene, hit.id);
    const sides = edgeCount(value);

    for (let edge = 0; edge < sides; edge += 1) {
      const curve = hostCurve(value, edge);
      if (curve === null) continue;

      const at = closestPoint(curve, world);
      const distance = vec.distance(world, at);
      if (distance >= bestDistance) continue;

      bestDistance = distance;
      best = {
        world: at,
        kind: SnapKind.Curve,
        host: hit.id,
        edge,
        parameter: parameterAt(curve, at),
      };
    }
  }

  return best;
}

function nearestCrossing(
  context: SnapContext,
  hits: readonly { id: EntityId; closest: Vec2 }[],
  world: Vec2,
  tolerance: number
): SnapResult | null {
  let best: SnapResult | null = null;
  let bestDistance = tolerance;

  for (let i = 0; i < hits.length; i++) {
    for (let j = i + 1; j < hits.length; j++) {
      const first = hits[i];
      const second = hits[j];
      if (first === undefined || second === undefined) continue;

      const a = asCurve(valueOf(context.scene, first.id));
      const b = asCurve(valueOf(context.scene, second.id));
      if (a === null || b === null) continue;

      // The index is the position in the routine's documented stable ordering, so a
      // point created here refers to the same crossing on every future reload.
      intersectCurves(a, b).forEach((candidate, index) => {
        const distance = vec.distance(world, candidate);
        if (distance >= bestDistance) return;
        bestDistance = distance;
        best = {
          world: candidate,
          kind: SnapKind.Intersection,
          crossing: [first.id, second.id],
          crossingIndex: index,
        };
      });
    }
  }
  return best;
}

/**
 * Constrains a drag to the nearest of a set of directions.
 *
 * The shift-drag behaviour of every editor. `divisions` of 8 gives the usual 45° steps.
 */
export function constrainDirection(from: Vec2, to: Vec2, divisions = 8): Vec2 {
  const delta = vec.subtract(to, from);
  const length = vec.length(delta);
  if (length === 0) return to;

  const step = (Math.PI * 2) / divisions;
  const snapped = Math.round(Math.atan2(delta.y, delta.x) / step) * step;
  return vec.addScaled(from, vec.fromAngle(snapped), length);
}

/**
 * Whether a snap result can host a locus point.
 *
 * Unbounded geometry can: a point on an infinite line is a perfectly good construction.
 * The check exists because the *parameter* means different things per curve kind, and a
 * caller that stored a `[0,1]` fraction for a line would place the point one world unit
 * along it rather than where the user clicked.
 */
export function canHostLocus(scene: Scene, id: EntityId): boolean {
  const entity = scene.document.entities.get(id);
  if (entity === undefined) return false;
  return asCurve(valueOf(scene, id)) !== null;
}

/** Clamps a locus parameter into the valid range for its host kind. */
export function clampParameter(host: Curve, parameter: number): number {
  switch (host.kind) {
    case 'line':
      return parameter;
    case 'ray':
      return Math.max(0, parameter);
    case 'segment':
    case 'arc':
      return clamp(parameter, 0, 1);
    case 'circle':
    case 'ellipse':
      // Closed curves wrap rather than clamp.
      return parameter - Math.floor(parameter);
  }
}

/** True when the entity's kind has no finite extent — used for cursor hints. */
export function isUnboundedEntity(scene: Scene, id: EntityId): boolean {
  const entity = scene.document.entities.get(id);
  return entity !== undefined && isUnbounded(kindOf(entity.source));
}
